import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Snackbar,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  AccountBalance as BankIcon,
  Receipt as TDSIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  PieChart as PieChartIcon,
} from '@mui/icons-material';
import { procurementService, Vendor, TDSCategory } from '../../services/procurementService';
import { api } from '../../services/apiClient';
import ExportButton from '../../components/common/ExportButton';

// Vendor category -- supply classification distinct from vendor_type
// (INDIVIDUAL/COMPANY/etc.). Backend column `vendors.vendor_category`
// (migration 036) is an ENUM of these values.
const VENDOR_CATEGORIES = [
  'RAW_MATERIAL',
  'COMPONENTS',
  'CONSUMABLES',
  'SERVICES',
  'CAPEX',
  'SUBCONTRACTOR',
  'JOBWORK',
  'LOGISTICS',
  'UTILITIES',
  'OTHER',
] as const;

// Vendor and TDSCategory interfaces imported from procurementService

// VendorReports Component
const VendorReports: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [tdsData, setTdsData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const { api } = await import('../../services/apiClient');
      // Both endpoints nest their payload under a `report` key (see
      // src/controllers/procurement.js getVendorPerformanceReport /
      // getTDSComplianceReport) — every `performanceData`/`tdsData` read
      // below expects the report's own fields, not the wrapper.
      const [performance, tds] = await Promise.all([
        api.get<{ report: any }>('/procurement/reports/vendor-performance'),
        api.get<{ report: any }>('/procurement/reports/tds-compliance')
      ]);
      setPerformanceData(performance.report);
      setTdsData(tds.report);
    } catch (err: any) {
      console.error('Failed to fetch reports:', err);
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Performance Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BusinessIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="primary.main">
                    {performanceData?.summary?.totalVendors || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Vendors
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="success" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="success.main">
                    {performanceData?.summary?.activeVendors || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Vendors
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BankIcon color="info" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="info.main">
                    ₹{((performanceData?.summary?.totalBusiness || 0) / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Business
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TDSIcon color="warning" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="warning.main">
                    ₹{((tdsData?.totals?.total_tds_deducted || 0) / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    TDS Deducted
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Performing Vendors */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Top 10 Performing Vendors
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Vendor Code</TableCell>
                  <TableCell>Company Name</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell align="right">Total Business</TableCell>
                  <TableCell align="right">Avg Order Value</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(performanceData?.topVendors || []).map((vendor: any, index: number) => (
                  <TableRow key={vendor.vendor_id} hover>
                    <TableCell>#{index + 1}</TableCell>
                    <TableCell>{vendor.vendor_code}</TableCell>
                    <TableCell>{vendor.company_name}</TableCell>
                    <TableCell>{vendor.state || '-'}</TableCell>
                    <TableCell align="right">{vendor.total_orders}</TableCell>
                    <TableCell align="right">₹{parseFloat(vendor.total_business || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell align="right">₹{parseFloat(vendor.avg_order_value || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell>
                      <Chip
                        label={vendor.is_active ? 'Active' : 'Inactive'}
                        color={vendor.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {(!performanceData?.topVendors || performanceData.topVendors.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No vendor data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Vendor Distribution */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* By Type */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                <PieChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Vendors by Type
              </Typography>
              {(performanceData?.vendorByType || []).map((item: any) => (
                <Box key={item.vendor_type} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2">{item.vendor_type || 'Unknown'}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {item.vendor_count} ({item.active_count} active)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(item.active_count / item.vendor_count) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
              {(!performanceData?.vendorByType || performanceData.vendorByType.length === 0) && (
                <Typography variant="body2" color="text.secondary">No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* By State */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Top States by Vendors
              </Typography>
              {(performanceData?.vendorByState || []).slice(0, 8).map((item: any) => (
                <Box key={item.state} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2">{item.state}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {item.vendor_count} vendors
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(item.vendor_count / (performanceData?.summary?.totalVendors || 1)) * 100}
                    color="secondary"
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
              {(!performanceData?.vendorByState || performanceData.vendorByState.length === 0) && (
                <Typography variant="body2" color="text.secondary">No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* TDS Compliance Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            TDS Compliance Summary - {tdsData?.financialYear || 'Current FY'}
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h4" color="primary.main">
                    {tdsData?.totals?.total_transactions || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Transactions</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h4" color="info.main">
                    ₹{((tdsData?.totals?.total_invoice_amount || 0) / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Invoice Amount</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h4" color="success.main">
                    {tdsData?.totals?.certificates_generated || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Certificates Generated</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h4" color="warning.main">
                    {tdsData?.totals?.pending_certificates || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Pending Certificates</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* TDS Section-wise Summary */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            TDS Deductions by Section
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>TDS Section</TableCell>
                  <TableCell align="right">Transactions</TableCell>
                  <TableCell align="right">Invoice Amount</TableCell>
                  <TableCell align="right">TDS Deducted</TableCell>
                  <TableCell align="right">Certificates</TableCell>
                  <TableCell align="right">Pending</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(tdsData?.sectionSummary || []).map((section: any) => (
                  <TableRow key={section.tds_section} hover>
                    <TableCell>
                      <Chip label={section.tds_section || 'N/A'} size="small" />
                    </TableCell>
                    <TableCell align="right">{section.transaction_count}</TableCell>
                    <TableCell align="right">₹{parseFloat(section.total_invoice_amount || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell align="right">₹{parseFloat(section.total_tds_deducted || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell align="right">
                      <Chip label={section.certificates_generated} color="success" size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={section.pending_certificates} color="warning" size="small" />
                    </TableCell>
                  </TableRow>
                ))}
                {(!tdsData?.sectionSummary || tdsData.sectionSummary.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No TDS deductions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

const VendorManagementPage: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [tdsCategories, setTDSCategories] = useState<TDSCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
    gstin: '',
    pan: '',
    vendor_type: 'COMPANY' as 'INDIVIDUAL' | 'COMPANY' | 'HUF' | 'PARTNERSHIP',
    tds_category: 'PROFESSIONAL_SERVICES',
    vendor_category: '',
    credit_days: 30,
    bank_name: '',
    bank_account: '',
    ifsc_code: '',
    is_active: true,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  // Fetch data
  useEffect(() => {
    fetchVendors();
    fetchTDSCategories();
  }, []);

  // Filter vendors based on search and filters
  useEffect(() => {
    let filtered = vendors;

    if (searchTerm) {
      filtered = filtered.filter(vendor =>
        vendor.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.vendor_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (vendor.email && vendor.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (stateFilter) {
      filtered = filtered.filter(vendor => vendor.state === stateFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(vendor => 
        statusFilter === 'active' ? vendor.is_active : !vendor.is_active
      );
    }

    setFilteredVendors(filtered);
  }, [vendors, searchTerm, stateFilter, statusFilter]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await procurementService.getVendors();
      setVendors(response.vendors);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const fetchTDSCategories = async () => {
    try {
      const response = await procurementService.getTDSCategories();
      setTDSCategories(response.categories);
    } catch (err) {
      console.error('Failed to fetch TDS categories:', err);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.company_name.trim()) {
      errors.company_name = 'Company name is required';
    }

    if (!formData.contact_person.trim()) {
      errors.contact_person = 'Contact person is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (formData.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstin)) {
      errors.gstin = 'Invalid GSTIN format';
    }

    if (formData.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan)) {
      errors.pan = 'Invalid PAN format';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // procurementService.createVendor/updateVendor build their own
      // camelCase payload and don't yet forward vendor_category, so send
      // the request directly here (same shape as the service, plus
      // vendorCategory) rather than dropping the field silently.
      const payload = {
        companyName: formData.company_name,
        contactPerson: formData.contact_person,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postal_code,
        country: formData.country,
        gstin: formData.gstin,
        pan: formData.pan,
        vendorType: formData.vendor_type,
        tdsCategory: formData.tds_category,
        vendorCategory: formData.vendor_category || null,
        creditDays: formData.credit_days,
        bankDetails: {
          bankName: formData.bank_name,
          bankAccount: formData.bank_account,
          ifscCode: formData.ifsc_code,
        },
        isActive: formData.is_active,
      };

      if (dialogMode === 'create') {
        await api.post('/procurement/vendors', payload);
        setSnackbar({ open: true, message: 'Vendor created successfully', severity: 'success' });
      } else {
        await api.put(`/procurement/vendors/${selectedVendor!.id}`, payload);
        setSnackbar({ open: true, message: 'Vendor updated successfully', severity: 'success' });
      }

      setOpenDialog(false);
      resetForm();
      await fetchVendors();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save vendor';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'India',
      gstin: '',
      pan: '',
      vendor_type: 'COMPANY',
      tds_category: 'PROFESSIONAL_SERVICES',
      vendor_category: '',
      credit_days: 30,
      bank_name: '',
      bank_account: '',
      ifsc_code: '',
      is_active: true,
    });
    setFormErrors({});
    setSelectedVendor(null);
  };

  const handleCreate = () => {
    setDialogMode('create');
    resetForm();
    setOpenDialog(true);
  };

  const handleEdit = (vendor: Vendor) => {
    setDialogMode('edit');
    setSelectedVendor(vendor);
    setFormData({
      company_name: vendor.company_name,
      contact_person: vendor.contact_person,
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address,
      city: vendor.city,
      state: vendor.state,
      postal_code: vendor.postal_code,
      country: vendor.country,
      gstin: vendor.gstin || '',
      pan: vendor.pan || '',
      vendor_type: vendor.vendor_type,
      tds_category: vendor.tds_category,
      vendor_category: (vendor as any).vendor_category || '',
      credit_days: vendor.credit_days,
      bank_name: vendor.bank_name || '',
      bank_account: vendor.bank_account || '',
      ifsc_code: vendor.ifsc_code || '',
      is_active: vendor.is_active,
    });
    setOpenDialog(true);
  };

  const handleView = (vendor: Vendor) => {
    setDialogMode('view');
    setSelectedVendor(vendor);
    setFormData({
      company_name: vendor.company_name,
      contact_person: vendor.contact_person,
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address,
      city: vendor.city,
      state: vendor.state,
      postal_code: vendor.postal_code,
      country: vendor.country,
      gstin: vendor.gstin || '',
      pan: vendor.pan || '',
      vendor_type: vendor.vendor_type,
      tds_category: vendor.tds_category,
      vendor_category: (vendor as any).vendor_category || '',
      credit_days: vendor.credit_days,
      bank_name: vendor.bank_name || '',
      bank_account: vendor.bank_account || '',
      ifsc_code: vendor.ifsc_code || '',
      is_active: vendor.is_active,
    });
    setOpenDialog(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          Vendor Management
        </Typography>
        {tabValue === 0 && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ExportButton
              rows={filteredVendors as any}
              columns={[
                { header: 'Vendor Code', key: 'vendor_code' }, { header: 'Company Name', key: 'company_name' },
                { header: 'GSTIN', key: 'gstin' }, { header: 'Email', key: 'email' }, { header: 'Phone', key: 'phone' },
                { header: 'City', key: 'city' }, { header: 'State', key: 'state' },
              ]}
              filename="vendors"
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              Add New Vendor
            </Button>
          </Box>
        )}
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={(_, newValue) => setTabValue(newValue)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Vendors" icon={<BusinessIcon />} iconPosition="start" />
        <Tab label="Reports & Analytics" icon={<AssessmentIcon />} iconPosition="start" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Vendors Tab Content */}
      {tabValue === 0 && (
        <>
        {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BusinessIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="primary.main">
                    {vendors.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Vendors
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BusinessIcon color="success" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="success.main">
                    {vendors.filter(v => v.is_active).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Vendors
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TDSIcon color="warning" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {vendors.filter(v => v.pan && v.pan.length === 10).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    TDS Compliant
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BankIcon color="info" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="info.main">
                    ₹{(vendors.reduce((sum, v) => sum + parseFloat(String(v.total_business || 0)), 0) / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Business
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Search Vendors"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  label="State"
                >
                  <MenuItem value="">All States</MenuItem>
                  {indianStates.map(state => (
                    <MenuItem key={state} value={state}>{state}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  label="Status"
                >
                  <MenuItem value="all">All Vendors</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                  <MenuItem value="inactive">Inactive Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredVendors.length} of {vendors.length} vendors
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Vendors Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Vendor Code</TableCell>
              <TableCell>Company Name</TableCell>
              <TableCell>Contact Person</TableCell>
              <TableCell>State</TableCell>
              <TableCell>TDS Category</TableCell>
              <TableCell>Total Business</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVendors.map((vendor) => (
              <TableRow key={vendor.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {vendor.vendor_code}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {vendor.company_name}
                    </Typography>
                    {vendor.gstin && (
                      <Typography variant="caption" color="text.secondary">
                        GSTIN: {vendor.gstin}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {vendor.contact_person}
                    </Typography>
                    {vendor.email && (
                      <Typography variant="caption" color="text.secondary">
                        {vendor.email}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{vendor.state}</TableCell>
                <TableCell>
                  <Chip 
                    label={vendor.tds_category.replace(/_/g, ' ')} 
                    size="small" 
                    variant="outlined"
                    color="info"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {vendor.totalBusinessFormatted || '₹0'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={vendor.is_active ? 'Active' : 'Inactive'}
                    color={vendor.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleView(vendor)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Vendor">
                      <IconButton size="small" onClick={() => handleEdit(vendor)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredVendors.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No vendors found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
        </>
      )}

      {/* Reports Tab Content */}
      {tabValue === 1 && <VendorReports />}

      {/* Vendor Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'create' && 'Add New Vendor'}
          {dialogMode === 'edit' && 'Edit Vendor'}
          {dialogMode === 'view' && 'Vendor Details'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.company_name}
                onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                error={!!formErrors.company_name}
                helperText={formErrors.company_name}
                disabled={dialogMode === 'view'}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Contact Person"
                value={formData.contact_person}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                error={!!formErrors.contact_person}
                helperText={formErrors.contact_person}
                disabled={dialogMode === 'view'}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                error={!!formErrors.email}
                helperText={formErrors.email}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                  label="State"
                  disabled={dialogMode === 'view'}
                >
                  {indianStates.map(state => (
                    <MenuItem key={state} value={state}>{state}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.postal_code}
                onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="GSTIN"
                value={formData.gstin}
                onChange={(e) => setFormData(prev => ({ ...prev, gstin: e.target.value.toUpperCase() }))}
                error={!!formErrors.gstin}
                helperText={formErrors.gstin || 'Format: 22AAAAA0000A1Z5'}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="PAN"
                value={formData.pan}
                onChange={(e) => setFormData(prev => ({ ...prev, pan: e.target.value.toUpperCase() }))}
                error={!!formErrors.pan}
                helperText={formErrors.pan || 'Format: AAAAA9999A'}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Vendor Type</InputLabel>
                <Select
                  value={formData.vendor_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, vendor_type: e.target.value as any }))}
                  label="Vendor Type"
                  disabled={dialogMode === 'view'}
                >
                  <MenuItem value="COMPANY">Company</MenuItem>
                  <MenuItem value="INDIVIDUAL">Individual</MenuItem>
                  <MenuItem value="HUF">HUF</MenuItem>
                  <MenuItem value="PARTNERSHIP">Partnership</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>TDS Category</InputLabel>
                <Select
                  value={formData.tds_category}
                  onChange={(e) => setFormData(prev => ({ ...prev, tds_category: e.target.value }))}
                  label="TDS Category"
                  disabled={dialogMode === 'view'}
                >
                  {tdsCategories.map(category => (
                    <MenuItem key={category.code} value={category.code}>
                      {category.description} ({category.section})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Vendor Category</InputLabel>
                <Select
                  value={formData.vendor_category}
                  onChange={(e) => setFormData(prev => ({ ...prev, vendor_category: e.target.value }))}
                  label="Vendor Category"
                  disabled={dialogMode === 'view'}
                >
                  <MenuItem value="">Not classified</MenuItem>
                  {VENDOR_CATEGORIES.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat.replace(/_/g, ' ')}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Credit Days"
                type="number"
                value={formData.credit_days}
                onChange={(e) => setFormData(prev => ({ ...prev, credit_days: parseInt(e.target.value) || 30 }))}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Bank Name"
                value={formData.bank_name}
                onChange={(e) => setFormData(prev => ({ ...prev, bank_name: e.target.value }))}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Bank Account"
                value={formData.bank_account}
                onChange={(e) => setFormData(prev => ({ ...prev, bank_account: e.target.value }))}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="IFSC Code"
                value={formData.ifsc_code}
                onChange={(e) => setFormData(prev => ({ ...prev, ifsc_code: e.target.value.toUpperCase() }))}
                disabled={dialogMode === 'view'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            {dialogMode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {dialogMode !== 'view' && (
            <Button
              variant="contained"
              onClick={handleSubmit}
            >
              {dialogMode === 'create' ? 'Create Vendor' : 'Update Vendor'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorManagementPage;